import React, {useEffect, useState, useRef, useContext, useMemo, useCallback} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Button,
  Modal,
  Divider,
  IconButton,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Switch,
  CircularProgress,
  FormControl,
  Select,
  TablePagination, LinearProgress, FormControlLabel, Checkbox, InputLabel, Tooltip, Chip,
  Menu, ListItemIcon, Dialog, DialogTitle, DialogContent, DialogActions,
  Drawer, InputAdornment, Autocomplete,
  Radio, RadioGroup, FormLabel,
} from '@mui/material';


import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';

import Layout from '../components/Layout';
import ProjectService from '../services/ProjectService';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DatePicker from 'react-datepicker';
import ConfirmModal from './modal/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import GenerateInvoiceModal from "./modal/GenerateInvoiceModal";
import ProjectInfoEntriesExpenses from "./sub-components/ProjectInfoEntriesExpenses";
import Roles from "../config/roles";
import {AuthContext} from "../contexts/AuthContext";
import ServiceService from "../services/ServiceService";
import {TbImageInPicture} from "react-icons/tb";
import CancelInvoiceModal from "./sub-components/CancelInvoiceModal";
import PROJECT_STATUS_OPTIONS from "./enums/projectStatusOptionsColors";
import INVOICE_STATUS_OPTIONS_COLORS from "./enums/invoiceStatusOptionsColors";
import {INVOICE_STATUS_OPTIONS, INVOICE_STATUS} from "./enums/invoiceStatusOptionsEnum";
import { useTranslation } from 'react-i18next';
import styles from "./css/ProjectInfo.module.css";
import InvoiceStatusBar from "./sub-components/InvoiceStatusBar";
import InvoiceStatusFilter from "./sub-components/InvoiceStatusFilter";
import ShareInvoiceModalSimple from "./sub-components/ShareInvoiceModal";
import CustomerService from "../services/CustomerService";
import UserService from "../services/UserService";
import PortalAuthService from "../services/PortalAuthService";
import ProjectTaskService from "../services/ProjectTaskService";
import EmployeeService from "../services/EmployeeService";
import ReportCompositionDrawer from './ReportCompositionDrawer';

/* ── read-only field row (label caption + value) ── */
const ProjFieldRow = ({ label, value, last }) => (
    <Box sx={{ py: 1.5, borderBottom: last ? 'none' : '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, mb: 0.4, lineHeight: 1 }}>
            {label}
        </Typography>
        {typeof value === 'string' || value == null ? (
            <Typography variant="body2" sx={{ color: '#1a2332', fontWeight: 500 }}>{value || '—'}</Typography>
        ) : value}
    </Box>
);

const SideLabel = ({ children }) => (
    <Typography sx={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1, mb: 2 }}>
        {children}
    </Typography>
);

/* ── Shared slide-over drawer shell ── */
const WorkspaceDrawer = ({ open, onClose, accentColor = '#427898', title, subtitle, saveLabel, onSave, width = 440, children }) => (
    <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: width }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
    >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Section color accent */}
            <Box sx={{ height: 3, bgcolor: accentColor, flexShrink: 0 }} />

            {/* Header */}
            <Box sx={{
                px: 3, pt: 2.5, pb: 2,
                borderBottom: '1px solid #EEF2F6', flexShrink: 0,
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
                <Box>
                    <Typography fontWeight={700} fontSize={16} color="#0F172A">{title}</Typography>
                    {subtitle && <Typography fontSize={12} color="#94A3B8" mt={0.3}>{subtitle}</Typography>}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
                {children}
            </Box>

            {/* Sticky footer */}
            <Box sx={{
                px: 3, py: 2.5, flexShrink: 0,
                borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC',
                display: 'flex', justifyContent: 'flex-end', gap: 1.5,
            }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={onSave}
                    sx={{
                        bgcolor: accentColor, textTransform: 'none', fontWeight: 600,
                        borderRadius: 1.5, boxShadow: 'none', px: 2.5,
                        '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' },
                    }}
                >
                    {saveLabel}
                </Button>
            </Box>
        </Box>
    </Drawer>
);

const ProjectInfo = () => {
  const { t } = useTranslation('projectInfo');

  const { id } = useParams();
  const { showToast } = useToast();
  const [project, setProject] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entryUsers, setEntryUsers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [services, setServices] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [totalNumberOfEntries, setTotalNumberOfEntries] = useState([]);
  const [totalNumberOfExpenses, setTotalNumberOfExpenses] = useState([]);
  const [totalNumberOfServices, setTotalNumberOfServices] = useState([]);
  const [reports, setReports] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customerPortalActive, setCustomerPortalActive] = useState(false);
  const [openProjectEntriesModal, setOpenProjectEntriesModal] = useState(false);

  const [openProjectExpenseModal, setOpenProjectExpenseModal] = useState(false);
  const [openProjectServiceModal, setOpenProjectServiceModal] = useState(false);

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [isGratisExpense, setIsGratisExpense] = useState(selectedExpense?.isGratis || false);
  const handleExpenseGratisChange = (event) => {
    setIsGratisExpense(event.target.checked);
    if (event.target.checked) {
      handleExpenseInputChange({ target: { name: "price", value: "" } });
    }
  };

  const [isEditProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [editedProject, setEditedProject] = useState(null);

  const [generateReportData, setGenerateReportData] = useState(null);
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [remoteSignDialogOpen, setRemoteSignDialogOpen] = useState(false);
  const [remoteSignReport, setRemoteSignReport] = useState(null);
  const [remoteSignSelectedEmail, setRemoteSignSelectedEmail] = useState('');
  const [remoteSignCustomEmail, setRemoteSignCustomEmail] = useState('');
  const [remoteSignSending, setRemoteSignSending] = useState(false);
  const [isConfirmDeleteProjectEntryOpen, setConfirmDeleteProjectEntryOpen] =
      useState(false);
  const [isConfirmDeleteProjectExpenseOpen, setConfirmDeleteProjectExpenseOpen] =
      useState(false);
  const [isConfirmDeleteProjectServiceOpen, setConfirmDeleteProjectServiceOpen] =
      useState(false);
  const [isDeletingProjectEntry, setIsDeletingProjectEntry] = useState(false);
  const [isDeletingProjectExpense, setIsDeletingProjectExpense] = useState(false);

  const [isDeletingProjectService, setIsDeletingProjectService] = useState(false);
  const [isDeletingProjectInvoice, setIsDeletingProjectInvoice] = useState(false);

  const [isConfirmDeleteProjectOpen, setConfirmDeleteProjectOpen] =
      useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  const [isConfirmDeleteProjectInvoiceOpen, setConfirmDeleteProjectInvoiceOpen] =
      useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  const [isEditInvoiceModalOpen, setEditInvoiceModalOpen] =
      useState(false);

  const [isGenerateInvoiceModalOpen, setGenerateInvoiceModalOpen] =
      useState(false);
  const [isGenerateReportModalOpen, setGenerateReportModalOpen] =
      useState(false);

  const [updateInvoice, setUpdateInvoice] = useState({
    status: '',
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allServices, setAllServices] = useState([]);

  const [selectedStatuses, setSelectedStatuses] = useState([]);

  useEffect(() => {
    const fetchAllServices = async () => {
      try {
        const response = await ServiceService.getAllServices(0, 100);
        setAllServices(response.content);
      } catch (error) {
        showToast(t('toasts.errorLoadingServices'), 'error');
        console.error('Failed to fetch services:', error);
      }
    };

    fetchAllServices();
  }, [t, showToast]);

  const normalize = (s) => String(s || "").toUpperCase();

  const filteredInvoices = useMemo(() => {
    if (!Array.isArray(invoices)) return [];
    if (!selectedStatuses || selectedStatuses.length === 0) return invoices; // "All"

    const wanted = new Set(selectedStatuses.map(normalize)); // e.g. PENDING/PAID/OVERDUE/CANCELLED
    return invoices.filter(inv => wanted.has(normalize(inv.status)));
  }, [invoices, selectedStatuses]);

  const selectedServiceData = allServices.find(
      (service) => service.name === selectedService?.service
  );
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEditInvoiceModal = () => {
    setEditInvoiceModalOpen(true);
  };

  const handleCloseEditInvoiceModal = () => {
    setEditInvoiceModalOpen(false);
  };

  const handleOpenGenerateInvoiceModal = () =>{
    setGenerateInvoiceModalOpen(true)
  }

  const handleOpenGenerateReportModal = () =>{
    setGenerateReportModalOpen(true)
  }
  const handleCloseGenerateInvoiceModal = () =>{
    setGenerateInvoiceModalOpen(false)
  }
  const handleCloseGenerateReportModal = () =>{
    setGenerateReportModalOpen(false)
  }



  const handleAddEntryClick = () => {
    setSelectedEntry({ description: '', workingHours: '', entryDate: new Date(), billable: true });
    setOpenProjectEntriesModal(true);
  };

  const handleCloseProjectEntryModal = () => {
    setOpenProjectEntriesModal(false);
    setSelectedEntry({ description: '', workingHours: '', entryDate: new Date(), billable: true });
    setValidationErrors({});
  };

  const handleCloseProjectServiceModal = () => {
    setOpenProjectServiceModal(false);
    setSelectedService({});
    setValidationErrors({});
  };
  const handleAddExpenseClick = () => {
    setSelectedExpense({
      description: '',
      price: '',
      isInvoiced: false
    });
    setOpenProjectExpenseModal(true);
  };
  const handleAddServiceClick = () => {
    setSelectedService({});
    setOpenProjectServiceModal(true);
  };

  const handleCloseProjectExpenseModal = () => {
    setOpenProjectExpenseModal(false);
    setSelectedExpense({ description: '', workingHours: '' });
    setValidationErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedEntry({ ...selectedEntry, [name]: value });
  };
  const handleExpenseInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedExpense({ ...selectedExpense, [name]: value });
  };

  useEffect(() => {
    const toastData = localStorage.getItem("TOAST_MESSAGE");
    if (toastData) {
      const { message, type } = JSON.parse(toastData);
      showToast(message, type);
      localStorage.removeItem("TOAST_MESSAGE");
    }
  }, [showToast]);

  const handleSaveProjectEntry = async () => {
    try {
      if (!selectedEntry.description && !selectedEntry.workingHours) {
        validateInputs();
        showToast(t('toasts.noDataToSave'), 'error');
        return;
      }

      if (!validateInputs()) {
        showToast(t('toasts.validationFix'), 'warning');
        return;
      }

      selectedEntry.project = project.id;
      if (selectedEntry.entryDate instanceof Date) {
        const d = selectedEntry.entryDate;
        const pad = (n) => String(n).padStart(2, '0');
        selectedEntry.entryDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      }
      let data;

      if (selectedEntry.id) {
        data = await ProjectService.updateProjectEntry(
            selectedEntry.id,
            selectedEntry
        );
        setEntries(
            entries.map((entry) => (entry.id === selectedEntry.id ? data : entry))
        );
        showToast(t("toasts.projectEntryUpdated"), 'success');
      } else {
        data = await ProjectService.createProjectEntry(selectedEntry);
        setEntries([...entries, data]);
        setTotalNumberOfEntries(totalNumberOfEntries + 1);
        showToast(t("toasts.projectCreated"), 'success');
      }

      handleCloseProjectEntryModal();
    } catch (error) {
      showToast(t('toasts.errorSavingProjectEntry'), 'error');
      console.error('Error saving contact:', error);
    }
  };

  const handleSaveProjectExpense = async () => {
    try {
      if (!selectedExpense.description) {
        showToast(t('validation.descriptionRequired'), 'error');
        return;
      }

      if (!isGratisExpense && (!selectedExpense.price || isNaN(selectedExpense.price))) {
        showToast(t('validation.priceRequiredNumber'), 'error');
        return;
      }

      const dto = {
        project: project.id,
        description: selectedExpense.description,
        price: isGratisExpense ? 0 : parseFloat(selectedExpense.price),
        invoiced: selectedExpense.isInvoiced || false
      };

      let data;
      if (selectedExpense.id) {
        data = await ProjectService.updateProjectExpense(selectedExpense.id, dto);
        setExpenses(expenses.map(exp => exp.id === data.id ? data : exp));
        setTotalNumberOfExpenses(totalNumberOfExpenses + 1);
        showToast(t("toasts.expenseUpdated"), 'success');
      } else {
        data = await ProjectService.createProjectExpense(dto);
        setExpenses([...expenses, data]);
        showToast(t("toasts.expenseCreated"), 'success');
      }

      handleCloseProjectExpenseModal();
    } catch (error) {
      showToast(t('toasts.errorSavingExpense'), 'error');
      console.error('Error saving expense:', error);
    }
  };

  const handleSaveProjectService = async () => {
    try {
      const dto = {
        subServiceId: selectedService.subServiceId,
        description: selectedService.description,
        quantity: selectedService.quantity
      };

      if (selectedService.id) {
        const updated = await ProjectService.updateProjectSubService(project.id, selectedService.id, dto);
        setServices(services.map((s) => (s.id === updated.id ? updated : s)));
        showToast(t('toasts.subserviceUpdated'), 'success');
      } else {
        const created = await ProjectService.addProjectSubService(project.id, dto);
        setServices([...services, created]);
        showToast(t('toasts.subserviceAdded'), 'success');
      }

      handleCloseProjectServiceModal();
    } catch (error) {
      showToast(t('toasts.errorSavingSubservice'), 'error');
      console.error('Error saving sub-service:', error);
    }
  };
  const [contacts, setContacts] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);

  const fetchProject = async () => {
    try {
      const data = await ProjectService.getProject(page, rowsPerPage, id);
      // data.project.locationAddress = "Marktmattenstrasse 7, 5600 Lenzburg"
      // data.project.customer.contact = "Munster Munster"
      setProject(data.project);
      setEntries(data.projectEntries.content);
      setTotalNumberOfEntries(data.projectEntries.totalElements);
      setExpenses(data.projectExpenses.content);
      setTotalNumberOfExpenses(data.projectExpenses.totalElements)
      setInvoices(data.invoices);
      setReports(data.reports);
      setEditedProject(data.project);
      const customer = await CustomerService.getCustomer(data.project.customer.id)
      setContacts(customer.customerContact);
      const portalStatus = await PortalAuthService.getPortalStatus(data.project.customer.id).catch(() => null);
      setCustomerPortalActive(portalStatus?.portalStatus === 'ACTIVE');
      const info = await ProjectService.getProjectInfo(id);
      setProjectInfo(info);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      showToast(t('toasts.errorLoadingProject'), 'error');
    }
  };

  useEffect(() => {
    fetchProject();
  }, [page, rowsPerPage]); // eslint-disable-line

  useEffect(() => {
    const fetchProjectSubServices = async () => {
      try {
        const subServicesResponse = await ProjectService.getProjectSubServices(id, page, rowsPerPage);
        setServices(subServicesResponse.content);
        setTotalNumberOfServices(subServicesResponse.totalElements)
      } catch (error) {
        console.error('Failed to fetch project sub-services:', error);
        showToast(t('toasts.errorLoadingSubservices'), 'error');
      }
    };

    const fetchProjectMaterials = async () => {
      try {
        const data = await ProjectService.getInventoryUsageByProject(id);
        setMaterials(data || []);
      } catch (error) {
        console.error('Failed to fetch project materials:', error);
      }
    };

    fetchProject();
    fetchProjectSubServices();
    fetchProjectMaterials();
  }, [id, page, rowsPerPage]); // eslint-disable-line

  const [anchorProjectEntryEl, setAnchorProjectEntryEl] = useState(null);
  const [anchorProjectExpenseEl, setAnchorProjectExpenseEl] = useState(null);
  const [anchorReportEntryEl, setAnchorReportEntryEl] = useState(null);
  const [anchorInvoiceEntryEl, setAnchorInvoiceEntryEl] = useState(null);
  const [anchorProjectServiceEl, setAnchorProjectServiceEl] = useState(null);

  const handleMoreProjectEntryClick = (event, projectEntry) => {
    setAnchorProjectEntryEl(event.currentTarget);
    setSelectedEntry(projectEntry);
  };

  const handleMoreProjectExpenseClick = (event, projectExpense) => {
    setAnchorProjectExpenseEl(event.currentTarget);
    setSelectedExpense(projectExpense);
  };

  const handleMoreProjectServiceClick = (event, projectService) => {
    setAnchorProjectServiceEl(event.currentTarget);
    setSelectedService(projectService);
  };

  const handleMoreReportEntryClick = (event, reportEntry) => {
    setAnchorReportEntryEl(event.currentTarget);
    setSelectedEntry(reportEntry);
  };

  const handleMoreInvoiceEntryClick = (event, invoiceEntry) => {
    setAnchorInvoiceEntryEl(event.currentTarget);
    setSelectedEntry(invoiceEntry);
  };

  const handleCloseProjectEntry = () => {
    setAnchorProjectEntryEl(null);
  };

  const handleCloseProjectExpense = () => {
    setAnchorProjectExpenseEl(null);
  };

  const handleCloseProjectService = () => {
    setAnchorProjectServiceEl(null);
  };

  const handleCloseReportEntry = () => {
    setAnchorReportEntryEl(null);
  };

  const handleCloseInvoiceEntry = () => {
    setAnchorInvoiceEntryEl(null);
  };

  const handleEditProjectEntry = (projectEntry) => {
    const ed = projectEntry.entryDate;
    setSelectedEntry({
      ...projectEntry,
      entryDate: ed ? new Date(ed + 'T00:00:00') : new Date(),
      billable: projectEntry.billable !== false,
    });
    setOpenProjectEntriesModal(true);
  };
  const handleEditProjectExpense = (projectExpense) => {
    setSelectedExpense(projectExpense);
    setIsGratisExpense(!!projectExpense.isGratis);
    setOpenProjectExpenseModal(true);
  };

  const handleEditProjectService = (projectService) => {
    setOpenProjectServiceModal(true);
    setSelectedService({id: projectService.id,
      service: projectService.serviceName,
      subServiceId: projectService.subServiceId,
      description: projectService.description,
      quantity: projectService.quantity});
  };

  const handlePreviewReportEntry = (reportEntry) => {
    setSelectedEntry(reportEntry);
    previewPdf(reportEntry.id);
  };

  const [shareInvoiceModal, setShareInvoiceModal] = useState(false);
  const handleShareInvoice = (invoiceEntry) => {
    setSelectedEntry(invoiceEntry);
    setShareInvoiceModal(true)
  }

  const [quickViewInvoice, setQuickViewInvoice] = useState(null);
  const [quickViewPdfUrl, setQuickViewPdfUrl] = useState(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);

  const handleOpenQuickView = async (invoice) => {
    setQuickViewInvoice(invoice);
    setQuickViewPdfUrl(null);
    setQuickViewLoading(true);
    try {
      const data = await ProjectService.downloadPdfInvoice(invoice.id);
      const url = URL.createObjectURL(new Blob([data.data], { type: 'application/pdf' }));
      setQuickViewPdfUrl(url);
    } catch {
      console.error('Failed to load invoice PDF for quick view');
    } finally {
      setQuickViewLoading(false);
    }
  };

  const handleCloseQuickView = () => {
    if (quickViewPdfUrl) URL.revokeObjectURL(quickViewPdfUrl);
    setQuickViewInvoice(null);
    setQuickViewPdfUrl(null);
  };

  const [quickViewReport, setQuickViewReport] = useState(null);
  const [quickViewReportPdfUrl, setQuickViewReportPdfUrl] = useState(null);
  const [quickViewReportLoading, setQuickViewReportLoading] = useState(false);

  const handleOpenReportQuickView = async (report) => {
    setQuickViewReport(report);
    setQuickViewReportPdfUrl(null);
    setQuickViewReportLoading(true);
    try {
      const data = await ProjectService.downloadPdfReport(report.id);
      const url = URL.createObjectURL(new Blob([data.data], { type: 'application/pdf' }));
      setQuickViewReportPdfUrl(url);
    } catch {} finally { setQuickViewReportLoading(false); }
  };

  const handleCloseReportQuickView = () => {
    if (quickViewReportPdfUrl) URL.revokeObjectURL(quickViewReportPdfUrl);
    setQuickViewReport(null);
    setQuickViewReportPdfUrl(null);
  };

  const handleDownloadInvoiceEntry = async (invoiceEntry) => {
    try {
      const data = await ProjectService.downloadPdfInvoice(invoiceEntry.id)
      const cd = data.headers["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^\";]+)/i);
      const serverName = match ? decodeURIComponent(match[1]) : `file-${id}`;
      const blobUrl = window.URL.createObjectURL(new Blob([data.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = serverName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  }

  const [isCancelInvoiceModalOpen, setIsCancelInvoiceModalOpen] = useState(false);
  const handleCancelInvoiceEntryModalOpen = () => {
    setIsCancelInvoiceModalOpen(true);
  }

  const handleDownloadReportEntry = async (reportEntry) => {
    try {
      const data = await ProjectService.downloadPdfReport(reportEntry.id)
      const cd = data.headers["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^\";]+)/i);
      const serverName = match ? decodeURIComponent(match[1]) : `file-${id}`;
      const blobUrl = window.URL.createObjectURL(new Blob([data.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = serverName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  }

  const handleEditInvoiceEntry = (invoiceEntry) => {
    setUpdateInvoice(invoiceEntry)
    handleOpenEditInvoiceModal();
  };

  const handlePublishInvoiceToPortal = async (invoiceEntry) => {
    try {
      const updated = await ProjectService.publishInvoiceToPortal(invoiceEntry.id);
      setInvoices(prev => prev.map(inv => inv.id === invoiceEntry.id ? { ...inv, publishedToPortal: updated.publishedToPortal, publishedToPortalAt: updated.publishedToPortalAt, status: updated.status, sentAt: updated.sentAt } : inv));
      showToast('Invoice sent to portal', 'success');
    } catch (error) {
      console.error('Failed to publish invoice to portal:', error);
      showToast('Failed to send invoice to portal', 'error');
    }
  };

  const handleUnpublishInvoiceFromPortal = async (invoiceEntry) => {
    try {
      const updated = await ProjectService.unpublishInvoiceFromPortal(invoiceEntry.id);
      setInvoices(prev => prev.map(inv => inv.id === invoiceEntry.id ? { ...inv, publishedToPortal: updated.publishedToPortal, publishedToPortalAt: null } : inv));
      showToast('Invoice removed from portal', 'info');
    } catch (error) {
      console.error('Failed to unpublish invoice from portal:', error);
      showToast('Failed to remove invoice from portal', 'error');
    }
  };

  const handleDeleteProjectEntry = (projectEntry) => {
    setConfirmDeleteProjectEntryOpen(true);
  };
  const handleDeleteProjectExpense = (projectEntry) => {
    setConfirmDeleteProjectExpenseOpen(true);
  };

  const handleDeleteProjectService = () => {
    setConfirmDeleteProjectServiceOpen(true);
  };

  const handleDeleteReportEntry = async (reportEntry) => {
    try {
      await ProjectService.deleteReport(reportEntry.id);
      setReports((prevReports) =>
          prevReports.filter((report) => report.id !== reportEntry.id)
      );
      showToast(t('toasts.reportDeleted'), 'warning');
    } catch (error) {
      showToast(t('toasts.errorDeletingReport'), 'error');
    } finally {
    }
  };

  const handleSendRemoteSignature = async () => {
    const resolvedEmail = remoteSignSelectedEmail === '__custom__'
      ? remoteSignCustomEmail.trim()
      : remoteSignSelectedEmail;
    if (!resolvedEmail) return;
    setRemoteSignSending(true);
    try {
      const updated = await ProjectService.sendReportForRemoteSignature(remoteSignReport.id, { customerEmail: resolvedEmail });
      setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
      showToast('Remote signature request sent', 'success');
      setRemoteSignDialogOpen(false);
    } catch {
      showToast('Failed to send remote signature request', 'error');
    } finally {
      setRemoteSignSending(false);
    }
  };

  const handleDeleteInvoiceEntry = async (invoiceEntry) => {
    try {
      await ProjectService.deleteInvoice(invoiceEntry.id);
      setInvoices((prevInvoices) =>
          prevInvoices.filter((invoice) => invoice.id !== invoiceEntry.id)
      );
      showToast(t('toasts.invoiceDeleted'), 'warning');
    } catch (error) {
      showToast(t('toasts.errorDeletingInvoice'), 'error');
    } finally {
    }
  };

  const handleDeleteProject = () => {
    setConfirmDeleteProjectOpen(true);
  };

  const handleConfirmDeleteProjectEntry = async () => {
    setIsDeletingProjectEntry(true);
    try {
      await ProjectService.deleteProjectEntry(selectedEntry.id);
      setEntries((prevEntries) =>
          prevEntries.filter((entry) => entry.id !== selectedEntry.id)
      );
      setIsDeletingProjectEntry(false);
      setConfirmDeleteProjectEntryOpen(false);
      showToast(t('toasts.projectEntryDeleted'), 'warning');
    } catch (error) {
      showToast(t('toasts.errorDeletingProjectEntry'), 'error');
      setIsDeletingProjectEntry(false);
    }
  };
  const { role } = useContext(AuthContext);

  useEffect(() => {
    if (role !== Roles.ADMIN) return;
    UserService.getAllUsers().then(setEntryUsers).catch(() => {});
  }, [role]);

  const handleConfirmDeleteProjectExpense = async () => {
    try {
      await ProjectService.deleteProjectExpense(selectedExpense.id);
      setExpenses(expenses.filter(exp => exp.id !== selectedExpense.id));
      showToast(t('toasts.expenseDeleted'), 'warning');
    } catch (error) {
      showToast(t('toasts.errorDeletingExpense'), 'error');
    } finally {
      setIsDeletingProjectExpense(false);
      setConfirmDeleteProjectExpenseOpen(false);
    }
  }

  const handleConfirmDeleteProjectInvoice = async () => {
    try {
      setIsDeletingProjectInvoice(true)
      await ProjectService.deleteInvoice(selectedEntry.id);
      setInvoices((prevInvoices) =>
          prevInvoices.filter((invoice) => invoice.id !== selectedEntry.id)
      );
      showToast(t('toasts.invoiceDeleted'), 'warning');
    } catch (error) {
      showToast(t('toasts.errorDeletingInvoice'), 'error');
    } finally {
      setIsDeletingProjectInvoice(false);
      setConfirmDeleteProjectInvoiceOpen(false);
    }
  };

  const handleConfirmDeleteProjectService = async () => {
    try {
      const result = await ProjectService.deleteProjectSubService(project.id, selectedService.id);
      if (result) {
        setServices(services.filter((s) => s.id !== selectedService.id));
        showToast(t('toasts.subserviceDeleted'), 'warning');
      }
    } catch (error) {
      showToast(t('toasts.errorDeletingSubservice'), 'error');
      console.error('Error deleting sub-service:', error);
    } finally {
      setIsDeletingProjectService(false);
      setConfirmDeleteProjectServiceOpen(false);
    }
  };

  const handleAddMaterial = async (dto) => {
    try {
      const created = await ProjectService.addInventoryUsage(project.id, dto);
      setMaterials(prev => [...prev, created]);
      showToast(dto.onDemand ? 'On-demand material added' : 'Material added — stock deducted', 'success');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to add material', 'error');
    }
  };

  const handleDeleteMaterial = async (usageId) => {
    try {
      await ProjectService.deleteInventoryUsage(project.id, usageId);
      setMaterials(prev => prev.filter(m => m.id !== usageId));
      showToast('Material removed — stock restored', 'warning');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to remove material', 'error');
    }
  };

  const handleConfirmDeleteProject = async () => {
    setIsDeletingProject(true);
    try {
      await ProjectService.deleteProject(project.id);
      showToast(t('toasts.projectDeleted'), 'warning');
      navigate('/projects');
    } catch (error) {
      setIsDeletingProjectEntry(false);
      showToast(t('toasts.errorDeletingProject'), 'error');
    }
  };

  const [validationErrors, setValidationErrors] = useState({});

  const validateInputs = () => {
    const errors = {};
    const letterPattern = /^[A-Za-z\s]+$/;
    const numberPattern = /^[0-9]+$/;

    const isEmpty = (value) => !value || value.toString().trim() === '';

    if (isEmpty(selectedEntry?.description)) {
      errors.description = t('validation.descriptionRequired');
    } else if (!letterPattern.test(selectedEntry.description)) {
      errors.description = t('validation.descriptionLettersOnly');
    }

    if (isEmpty(selectedEntry?.workingHours)) {
      errors.workingHours = t('validation.hoursRequired');
    } else if (!numberPattern.test(selectedEntry.workingHours.toString())) {
      errors.workingHours = t('validation.hoursNumbersOnly');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenEditProjectModal = () => {
    setEditProjectModalOpen(true);
  };

  const handleSaveProjectChanges = async () => {

    try {
      editedProject.customer = project.customer.id;
      editedProject.startDate = new Date(editedProject.startDate);
      editedProject.endDate = new Date(editedProject.endDate);
      const data = await ProjectService.updateProject(
          project.id,
          editedProject
      );
      setProject(data);

      handleCloseEditProjectModal();
      showToast(t('toasts.projectUpdated'), 'success');
    } catch (error) {
      console.error('Error saving project:', error);
      showToast(t('toasts.errorUpdatingProject'), 'error');
    }
  };

  const handleCloseEditProjectModal = () => {
    setEditProjectModalOpen(false);
  };

  const previewPdf = async (reportId) => {
    try {
      const data = await ProjectService.downloadReport(reportId);
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      console.error('Error fetching PDF:', error);
    }
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    const formData = new FormData();
    formData.append(
        "request",
        new Blob([JSON.stringify(generateReportData)], { type: "application/json" })
    );

    if (imageToReport) {
      formData.append("imageToInvoice", imageToReport, imageToReportName.name);
    }

    const created = await ProjectService.generateReport(project.id, formData);
    setIsGeneratingReport(false);
    setReports(prev => [created, ...prev]);
    showToast(t('toasts.reportCreated'), 'success');
  };

  const handleUpdateInvoice = async () => {
    try {
      const data = await ProjectService.updateInvoice(updateInvoice);
      setInvoices(
          invoices.map((entry) =>
              entry.id === updateInvoice.id
                  ? { ...entry, status: data.status }
                  : entry
          )
      );

      showToast(t('toasts.invoiceUpdated'), 'success');
      handleCloseEditInvoiceModal();
    } catch (error) {
      showToast(t('toasts.errorUpdatingInvoice'), 'error');
    } finally {

    }
  }
  const [imageToReport, setImageToReport] = useState(null);
  const [imageToReportName, setImageToReportName] = useState(null);
  const handleAddImageToReport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert(t('toasts.logoInvalid'));
      return;
    }
    setImageToReport(file);
    setImageToReportName(file.name);
  }

  const calcProgress = () => {
    const invoiced = project?.invoicedAmount || 0;
    const base = project?.contractedValue || project?.totalAmount || 0;
    if (base === 0) return invoiced > 0 ? 100 : 0;
    return (invoiced / base) * 100;
  };

  const progressBarColor = () => {
    const pct = calcProgress();
    if (pct > 100) return '#EF4444';
    if (pct >= 90) return '#F59E0B';
    return '#427898';
  };

  const handleServiceFieldChange = (field, value) => {
    if (field === "service") {
      const service = allServices.find((s) => s.name === value);
      setSelectedService((prev) => ({
        ...prev,
        service: value,
        subServiceId: service?.subServices[0]?.id || null,
      }));
    } else if (field === "subServiceId") {
      setSelectedService((prev) => ({
        ...prev,
        subServiceId: value,
      }));
    } else {
      setSelectedService((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const [dateError, setDateError] = useState("");

  const handleValidateAndGenerate = () => {
    if (
        generateReportData?.startDate &&
        generateReportData?.endDate &&
        generateReportData.startDate > generateReportData.endDate
    ) {
      setDateError(t('validation.startBeforeEnd'));
      return;
    }

    setDateError("");
    handleGenerateReport();
  };


  // ─── Notes state (CRM side) ───────────────────────────────────────────────
  const [projectNotes, setProjectNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteVisibility, setNewNoteVisibility] = useState('SHARED');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState(null);
  const notesEndRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    if (!id) return;
    setNotesLoading(true);
    try {
      const data = await ProjectService.getNotes(id);
      setProjectNotes(data || []);
    } catch (e) {
      // non-critical
    } finally {
      setNotesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (project) fetchNotes();
  }, [project, fetchNotes]);

  const handleAddProjectNote = async () => {
    if (!newNoteContent.trim()) return;
    setAddingNote(true);
    try {
      await ProjectService.addNote(id, { content: newNoteContent.trim(), visibility: newNoteVisibility });
      setNewNoteContent('');
      fetchNotes();
    } catch (e) {
      showToast('Failed to add note', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleSaveNoteEdit = async (noteId) => {
    if (!editingNoteContent.trim()) return;
    try {
      await ProjectService.editNote(id, noteId, { content: editingNoteContent.trim() });
      setEditingNoteId(null);
      fetchNotes();
    } catch (e) {
      showToast('Failed to save note', 'error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    setConfirmDeleteNoteId(null);
    try {
      await ProjectService.deleteNote(id, noteId);
      fetchNotes();
    } catch (e) {
      showToast('Failed to delete note', 'error');
    }
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const TAB_NAME_MAP = { overview: 0, 'work-log': 1, reports: 2, invoices: 3, notes: 4, tasks: 5 };
  const resolveTab = (val) => {
    if (val == null) return 0;
    if (typeof val === 'number') return val;
    return TAB_NAME_MAP[val] ?? 0;
  };
  const [pageTab, setPageTab] = useState(resolveTab(location.state?.agentTab));

  // ── Tasks tab state ──
  const [projectTasks, setProjectTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', taskDate: '', startTime: '', endTime: '', location: '', status: 'PLANNED', employeeIds: [] });
  const [savingTask, setSavingTask] = useState(false);
  const [taskEmployees, setTaskEmployees] = useState([]);
  const TASK_STATUS_COLORS = { PLANNED: { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' }, IN_PROGRESS: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' }, DONE: { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' }, CANCELLED: { bg: '#F8FAFC', text: '#94A3B8', border: '#E2E8F0' } };

  useEffect(() => {
    if (location.state?.agentTab != null) {
      setPageTab(resolveTab(location.state.agentTab));
      window.history.replaceState({}, '');
    }
  }, [location.state?.agentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectTasks = useCallback(async () => {
    if (!id) return;
    setTasksLoading(true);
    try {
      const data = await ProjectTaskService.getByProject(id);
      setProjectTasks(data || []);
    } catch { /* silent */ }
    finally { setTasksLoading(false); }
  }, [id]);

  useEffect(() => {
    if (pageTab === (role === Roles.ADMIN ? 5 : undefined)) {
      loadProjectTasks();
      EmployeeService.getEmployees(0, 200).then(d => setTaskEmployees(d?.content || [])).catch(() => {});
    }
  }, [pageTab, role, loadProjectTasks]);

  const openTaskAdd = () => {
    setEditingTask(null);
    setTaskForm({ title: '', description: '', taskDate: new Date().toISOString().split('T')[0], startTime: '', endTime: '', location: '', status: 'PLANNED', employeeIds: [] });
    setTaskDrawerOpen(true);
  };
  const openTaskEdit = (task) => {
    setEditingTask(task);
    setTaskForm({ title: task.title || '', description: task.description || '', taskDate: task.taskDate || '', startTime: task.startTime || '', endTime: task.endTime || '', location: task.location || '', status: task.status || 'PLANNED', employeeIds: task.assignedEmployees?.map(e => e.id) || [] });
    setTaskDrawerOpen(true);
  };
  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) { showToast('Title is required', 'error'); return; }
    setSavingTask(true);
    try {
      const payload = { ...taskForm, projectId: parseInt(id), startTime: taskForm.startTime || null, endTime: taskForm.endTime || null, description: taskForm.description || null, location: taskForm.location || null };
      if (editingTask) { await ProjectTaskService.update(editingTask.id, payload); showToast('Task updated', 'success'); }
      else { await ProjectTaskService.create(payload); showToast('Task created', 'success'); }
      setTaskDrawerOpen(false);
      loadProjectTasks();
    } catch { showToast('Failed to save task', 'error'); }
    finally { setSavingTask(false); }
  };
  const handleDeleteTask = async (taskId) => {
    try { await ProjectTaskService.delete(taskId); showToast('Task deleted', 'success'); loadProjectTasks(); }
    catch { showToast('Failed to delete task', 'error'); }
  };

  const groupedProjectTasks = projectTasks.reduce((acc, t) => {
    const key = t.taskDate || 'No date';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const sortedTaskDates = Object.keys(groupedProjectTasks).sort();
  const fmtTaskDateHeader = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; } };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
  const statusColor = PROJECT_STATUS_OPTIONS.find(o => o.label === project?.status)?.color || '#94a3b8';

  return (
      <Layout fullBleed>

        {/* ── Sticky page header ── */}
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: '#fff', borderBottom: '1px solid #EEF2F6',
          px: { xs: 2, md: 3 },
          display: 'flex', alignItems: 'center', gap: 1.5,
          height: 56, flexShrink: 0,
        }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => navigate('/projects')}
            sx={{ color: '#64748B', textTransform: 'none', fontWeight: 500, fontSize: 13, minWidth: 0, px: 1, '&:hover': { bgcolor: '#F8FAFC' } }}
          >
            Projects
          </Button>
          <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 140, sm: 280 } }}>
            {project?.name || '—'}
          </Typography>
          {project?.status && (
            <Chip
              size="small"
              label={t(`statuses.${project.status}`)}
              sx={{ bgcolor: statusColor + '18', color: statusColor, fontWeight: 600, fontSize: 11, border: `1px solid ${statusColor}30`, height: 22, flexShrink: 0 }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {role === Roles.ADMIN && (
            <>
              <Tooltip title="New Report">
                <IconButton size="small" onClick={() => { setEditingReport(null); setReportDrawerOpen(true); setPageTab(2); }} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                  <DescriptionIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('buttons.generateInvoice')}>
                <IconButton size="small" onClick={handleOpenGenerateInvoiceModal} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                  <ReceiptLongIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('buttons.editProject')}>
                <IconButton size="small" onClick={handleOpenEditProjectModal} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                  <EditIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={(e) => setMenuAnchorEl(e.currentTarget)} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <MoreVertIcon sx={{ fontSize: 19 }} />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
                PaperProps={{ sx: { borderRadius: 1.5, border: '1px solid #EEF2F6', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: 180 } }}
              >
                <Divider />
                <MenuItem onClick={() => { setMenuAnchorEl(null); handleDeleteProject(); }} sx={{ color: '#EF4444', fontSize: 14 }}>
                  <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} /></ListItemIcon>
                  {t('buttons.deleteProject')}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {/* ── Tab bar (sticky below header) ── */}
        <Box sx={{ position: 'sticky', top: 56, zIndex: 9, bgcolor: '#fff', borderBottom: '1px solid #EEF2F6', px: { xs: 2, md: 3 } }}>
          <Tabs
            value={pageTab}
            onChange={(_, v) => setPageTab(v)}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, color: '#64748B', fontSize: 14, minHeight: 44, py: 0 },
              '& .Mui-selected': { color: '#427898', fontWeight: 600 },
              '& .MuiTabs-indicator': { backgroundColor: '#427898' },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Work Log" />
            {role === Roles.ADMIN && <Tab label="Reports" />}
            {role === Roles.ADMIN && <Tab label="Invoices" />}
            <Tab label="Notes" />
            {role === Roles.ADMIN && <Tab label="Tasks" />}
          </Tabs>
        </Box>

        {/* ── Tab content ── */}
        <Box sx={{ bgcolor: '#F8FAFC', minHeight: 'calc(100vh - 104px)' }}>

          {/* ── Overview ── */}
          {pageTab === 0 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Project info — full width */}
              <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 3 }}>
                <SideLabel>{t('sections.projectInfo')}</SideLabel>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, columnGap: 4 }}>
                  <ProjFieldRow label={t('labels.projectName')} value={project?.name} />
                  <ProjFieldRow label={t('labels.location')}    value={project?.location} />
                  <ProjFieldRow label={t('labels.startDate')}   value={fmtDate(project?.startDate)} />
                  <ProjFieldRow label={t('labels.endDate')}     value={fmtDate(project?.endDate)} />
                  <ProjFieldRow
                    label={t('labels.status')}
                    value={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={500} sx={{ color: '#1a2332' }}>
                          {t(`statuses.${project?.status}`)}
                        </Typography>
                      </Box>
                    }
                  />
                  {role === Roles.ADMIN && project?.pricePerHour != null && (
                    <ProjFieldRow
                      label={t('pricing.pricePerHour')}
                      value={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: '#64748b' }} />
                          <Typography variant="body2" fontWeight={500} sx={{ color: '#1a2332' }}>
                            CHF {project?.pricePerHour?.toLocaleString() || '—'} / h
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                    <ProjFieldRow label={t('labels.description')} value={project?.description} last />
                  </Box>
                </Box>
              </Paper>

              {/* Context cards — 2-column grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

                {/* Financial (admin only) */}
                {role === Roles.ADMIN && (
                  <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 3 }}>
                    <SideLabel>Financial</SideLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {[
                        { label: t('labels.totalProjectCost'), value: project?.totalAmount, highlight: true },
                        { label: t('labels.contractedValue'),  value: project?.contractedValue },
                        { label: t('labels.budget'),           value: project?.budget },
                        { label: t('labels.invoicedAmount'),   value: project?.invoicedAmount },
                      ].map(({ label, value, highlight }) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <Typography sx={{ fontSize: 12, color: '#64748B' }}>{label}</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: highlight ? 700 : 600, color: highlight ? '#427898' : '#1E293B' }}>
                            {value != null ? `CHF ${Number(value).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </Typography>
                        </Box>
                      ))}
                      {project?.contractedValue != null && (() => {
                        const remaining = (project.contractedValue || 0) - (project.invoicedAmount || 0);
                        return (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: 0.5, borderTop: '1px dashed #EEF2F6' }}>
                            <Typography sx={{ fontSize: 12, color: '#64748B' }}>Remaining to invoice</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: remaining >= 0 ? '#16A34A' : '#EF4444' }}>
                              {remaining >= 0 ? '' : '−'}CHF {Math.abs(remaining).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        );
                      })()}
                      <Box sx={{ pt: 0.5 }}>
                        <LinearProgress variant="determinate" value={Math.min(calcProgress(), 100)}
                          sx={{ height: 5, borderRadius: 3, bgcolor: '#e8edf2', '& .MuiLinearProgress-bar': { bgcolor: progressBarColor(), borderRadius: 3 } }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                            {project?.contractedValue != null ? 'of contracted value' : 'of project cost'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                            {calcProgress().toFixed(1)}% invoiced
                          </Typography>
                        </Box>
                        {project?.contractedValue != null && (project?.invoicedAmount || 0) > (project?.contractedValue || 0) && (
                          <Box sx={{ mt: 1, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography sx={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}>
                              ⚠ CHF {((project.invoicedAmount || 0) - (project.contractedValue || 0)).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} over contract
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                )}

                {/* Invoice status summary */}
                <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 3 }}>
                  <InvoiceStatusBar projectInfo={projectInfo} />
                </Paper>

                {/* Customer Info */}
                <Paper elevation={0}
                  sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 3, cursor: project?.customer?.id ? 'pointer' : 'default', '&:hover': project?.customer?.id ? { boxShadow: '0 0 0 2px #42789840' } : {} }}
                  onClick={() => project?.customer?.id && navigate(`/customers/${project.customer.id}`)}>
                  <SideLabel>{t('sidebar.customerInfo')}</SideLabel>
                  <ProjFieldRow label="Name"  value={project?.customer?.name} />
                  <ProjFieldRow label="Email" value={project?.customer?.email} />
                  <ProjFieldRow label="Phone" value={project?.customer?.phone} last />
                </Paper>

                {/* Customer Contact */}
                <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 3 }}>
                  <SideLabel>{t('sidebar.customerContact')}</SideLabel>
                  {project?.customerContact ? (
                    <>
                      <ProjFieldRow label="Name" value={`${project.customerContact.firstName} ${project.customerContact.lastName}`} />
                      <ProjFieldRow label="Email" value={project.customerContact.email} />
                      <ProjFieldRow label="Phone" value={project.customerContact.phone || project.customerContact.mobile} />
                      {contacts?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <select
                            value={project.customerContact.id}
                            onChange={async (e) => {
                              const contactId = parseInt(e.target.value);
                              try {
                                const updated = await ProjectService.updateProject(project.id, {
                                  ...project,
                                  customer: project.customer.id,
                                  customerContactId: contactId,
                                });
                                setProject(updated);
                                showToast('Contact updated', 'success');
                              } catch { showToast('Failed to update contact', 'error'); }
                            }}
                            style={{ fontSize: 11, color: '#427898', border: 'none', background: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                          >
                            {contacts.map(c => (
                              <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                            ))}
                          </select>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box>
                      <Typography sx={{ fontSize: 12, color: '#94A3B8', mb: 1 }}>No contact assigned</Typography>
                      {contacts?.length > 0 && (
                        <select
                          value=""
                          onChange={async (e) => {
                            const contactId = parseInt(e.target.value);
                            if (!contactId) return;
                            try {
                              const updated = await ProjectService.updateProject(project.id, {
                                ...project,
                                customer: project.customer.id,
                                customerContactId: contactId,
                              });
                              setProject(updated);
                              showToast('Contact assigned', 'success');
                            } catch { showToast('Failed to assign contact', 'error'); }
                          }}
                          style={{ fontSize: 12, color: '#427898', border: '1px solid #CBD5E1', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', background: '#fff', width: '100%' }}
                        >
                          <option value="">Assign a contact…</option>
                          {contacts.map(c => (
                            <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                          ))}
                        </select>
                      )}
                    </Box>
                  )}
                </Paper>

              </Box>
            </Box>
          )}

          {/* ── Work Log ── */}
          {pageTab === 1 && (
            <Box sx={{ p: 3 }}>
              <ProjectInfoEntriesExpenses
                  project={project}
                  entries={entries}
                  expenses={expenses}
                  services={services}
                  materials={materials}
                  handleAddEntryClick={handleAddEntryClick}
                  handleMoreProjectEntryClick={handleMoreProjectEntryClick}
                  totalNumberOfEntries={totalNumberOfEntries}
                  totalNumberOfExpenses={totalNumberOfExpenses}
                  totalNumberOfServices={totalNumberOfServices}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  anchorProjectEntryEl={anchorProjectEntryEl}
                  handleCloseProjectEntry={handleCloseProjectEntry}
                  handleEditProjectEntry={handleEditProjectEntry}
                  handleDeleteProjectEntry={handleDeleteProjectEntry}
                  selectedEntry={selectedEntry}
                  handleAddExpenseClick={handleAddExpenseClick}
                  anchorProjectExpenseEl={anchorProjectExpenseEl}
                  handleCloseProjectExpense={handleCloseProjectExpense}
                  handleEditProjectExpense={handleEditProjectExpense}
                  handleDeleteProjectExpense={handleDeleteProjectExpense}
                  selectedExpense={selectedExpense}
                  handleMoreProjectExpenseClick={handleMoreProjectExpenseClick}
                  handleAddServiceClick={handleAddServiceClick}
                  anchorProjectServiceEl={anchorProjectServiceEl}
                  handleCloseProjectService={handleCloseProjectService}
                  handleEditProjectService={handleEditProjectService}
                  handleDeleteProjectService={handleDeleteProjectService}
                  selectedService={selectedService}
                  handleMoreProjectServiceClick={handleMoreProjectServiceClick}
                  handleAddMaterial={handleAddMaterial}
                  handleDeleteMaterial={handleDeleteMaterial}
                  role={role}
                  entryUsers={entryUsers}
              />
            </Box>
          )}

          {/* ── Reports (admin only) ── */}
          {role === Roles.ADMIN && pageTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>Reports</Typography>
                <Button variant="contained" size="small" startIcon={<AddIcon />}
                  onClick={() => { setEditingReport(null); setReportDrawerOpen(true); }}
                  sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#427898', '&:hover': { bgcolor: '#356480' }, boxShadow: 'none', borderRadius: 1.5 }}>
                  New Report
                </Button>
              </Box>
              <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Report</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Type</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date</TableCell>
                      <TableCell sx={{ width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, py: 4 }}>
                          No reports yet. Click "New Report" to create one.
                        </TableCell>
                      </TableRow>
                    )}
                    {reports.map((report) => {
                      const STATUS_META = {
                        DRAFT:                      { label: 'Draft',             color: '#94a3b8' },
                        PUBLISHED:                  { label: 'Published',         color: '#64748b' },
                        SIGNED:                     { label: 'Signed',            color: '#22c55e' },
                        REFUSED:                    { label: 'Refused',           color: '#ef4444' },
                        CUSTOMER_NOT_AVAILABLE:     { label: 'Not Available',     color: '#f59e0b' },
                        AWAITING_REMOTE_SIGNATURE:  { label: 'Awaiting Signature',color: '#3b82f6' },
                        REMOTE_SIGNATURE_EXPIRED:   { label: 'Expired',           color: '#f97316' },
                        CONFIRMED_WITHOUT_SIGNATURE:{ label: 'Confirmed',         color: '#64748b' },
                      };
                      const sm = STATUS_META[report.status] || { label: report.status, color: '#94a3b8' };
                      const isFieldService = report.type === 'FIELD_SERVICE';
                      const isDraft = report.status === 'DRAFT';
                      return (
                        <TableRow key={report.id} sx={{
                          '&:hover': { backgroundColor: '#F8FAFC' },
                          '&:hover .row-actions': { opacity: 1 },
                          '& .row-actions': { opacity: 0, transition: 'opacity 0.15s' },
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip size="small" label={sm.label} sx={{ bgcolor: sm.color + '18', color: sm.color, fontWeight: 600, fontSize: 11, border: `1px solid ${sm.color}30`, height: 22 }} />
                              {report.publishedToPortal && (
                                <Chip label="Portal" size="small"
                                  icon={<CloudUploadIcon style={{ fontSize: 11 }} />}
                                  sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, fontSize: 10, borderRadius: '6px', height: 20, '& .MuiChip-icon': { color: '#1565C0', ml: '6px' } }}
                                />
                              )}
                              {report.emailSentAt && (
                                <Tooltip title={`Sent by ${report.emailSentByName || 'unknown'} on ${new Date(report.emailSentAt).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}>
                                  <Chip label="Email Sent" size="small"
                                    icon={<EmailIcon style={{ fontSize: 11 }} />}
                                    sx={{ bgcolor: '#F0FDF4', color: '#16A34A', fontWeight: 600, fontSize: 10, borderRadius: '6px', height: 20, '& .MuiChip-icon': { color: '#16A34A', ml: '6px' } }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#1E293B', fontWeight: 500 }}>{report.displayName || report.name}</TableCell>
                          <TableCell>
                            <Chip size="small" label={isFieldService ? 'Field Service' : 'Internal'}
                              sx={{ bgcolor: isFieldService ? '#42789818' : '#F1F5F9', color: isFieldService ? '#427898' : '#64748B', fontWeight: 600, fontSize: 11, height: 22 }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#64748B' }}>
                            {report.createdAt ? new Date(report.createdAt).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}
                          </TableCell>
                          <TableCell>
                            <Box className="row-actions" sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end', alignItems: 'center' }}>
                              {isDraft && isFieldService && (
                                <Tooltip title="Present to Customer">
                                  <Button size="small" variant="outlined"
                                    onClick={() => window.open(`/sign-report/${report.id}`, '_blank')}
                                    sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, borderColor: '#427898', color: '#427898', height: 26, px: 1.5, minWidth: 0 }}>
                                    Present →
                                  </Button>
                                </Tooltip>
                              )}
                              {isDraft && !isFieldService && (
                                <Tooltip title="Publish">
                                  <Button size="small" variant="outlined"
                                    onClick={async () => {
                                      try {
                                        const updated = await ProjectService.publishReport(report.id);
                                        setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
                                        showToast('Report published', 'success');
                                      } catch { showToast('Failed to publish', 'error'); }
                                    }}
                                    sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, borderColor: '#427898', color: '#427898', height: 26, px: 1.5, minWidth: 0 }}>
                                    Publish
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip title="Quick view">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenReportQuickView(report); }} sx={{ color: '#94A3B8', '&:hover': { color: '#427898', bgcolor: '#EFF6FF' } }}>
                                  <VisibilityIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <IconButton size="small" onClick={(e) => handleMoreReportEntryClick(e, report)} sx={{ color: '#94A3B8', '&:hover': { color: '#427898' } }}>
                                <MoreVertIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Menu
                  anchorEl={anchorReportEntryEl}
                  open={Boolean(anchorReportEntryEl)}
                  onClose={handleCloseReportEntry}
                  PaperProps={{ sx: { borderRadius: 1.5, border: '1px solid #EEF2F6', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: 160 } }}
                >
                  <MenuItem onClick={() => { navigate(`/reports/${selectedEntry?.id}`, { state: { report: selectedEntry, projectId: id } }); handleCloseReportEntry(); }} sx={{ fontSize: 14 }}>
                    <ListItemIcon><OpenInNewIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                    Open detail page
                  </MenuItem>
                  <MenuItem onClick={() => { handleOpenReportQuickView(selectedEntry); handleCloseReportEntry(); }} sx={{ fontSize: 14 }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                    Quick view
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  {selectedEntry?.status === 'DRAFT' && (
                    <MenuItem onClick={() => { setEditingReport(selectedEntry); setReportDrawerOpen(true); handleCloseReportEntry(); }} sx={{ fontSize: 14 }}>Edit</MenuItem>
                  )}
                  <MenuItem onClick={() => { handleDownloadReportEntry(selectedEntry); handleCloseReportEntry(); }} sx={{ fontSize: 14 }}>{t('common.download')}</MenuItem>
                  {selectedEntry?.status === 'REFUSED' && (
                    <MenuItem onClick={async () => {
                      try {
                        const updated = await ProjectService.reopenReport(selectedEntry.id);
                        setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
                        showToast('Report reopened', 'success');
                      } catch { showToast('Failed to reopen', 'error'); }
                      handleCloseReportEntry();
                    }} sx={{ fontSize: 14 }}>Reopen</MenuItem>
                  )}
                  {(selectedEntry?.status === 'CUSTOMER_NOT_AVAILABLE' || selectedEntry?.status === 'REFUSED') && (
                    <MenuItem onClick={async () => {
                      const note = window.prompt('Confirmation note (optional):');
                      if (note === null) { handleCloseReportEntry(); return; }
                      try {
                        const updated = await ProjectService.confirmReportWithoutSignature(selectedEntry.id, { note });
                        setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
                        showToast('Report confirmed', 'success');
                      } catch { showToast('Failed to confirm', 'error'); }
                      handleCloseReportEntry();
                    }} sx={{ fontSize: 14 }}>Confirm without signature</MenuItem>
                  )}
                  {selectedEntry?.status === 'CUSTOMER_NOT_AVAILABLE' && (
                    <MenuItem onClick={() => {
                      const defaultEmail = project?.customerContact?.email || '';
                      setRemoteSignSelectedEmail(defaultEmail || contacts?.[0]?.email || '');
                      setRemoteSignCustomEmail('');
                      setRemoteSignReport(selectedEntry);
                      setRemoteSignDialogOpen(true);
                      handleCloseReportEntry();
                    }} sx={{ fontSize: 14 }}>Send for remote signature</MenuItem>
                  )}
                  {(selectedEntry?.status === 'SIGNED' || selectedEntry?.status === 'CONFIRMED_WITHOUT_SIGNATURE') && customerPortalActive && (
                    selectedEntry?.publishedToPortal ? (
                      <MenuItem onClick={async () => {
                        try {
                          const updated = await ProjectService.unpublishReportFromPortal(selectedEntry.id);
                          setReports(prev => prev.map(r => r.id === updated.id ? { ...r, publishedToPortal: updated.publishedToPortal } : r));
                          showToast('Removed from portal', 'info');
                        } catch { showToast('Failed to unpublish', 'error'); }
                        handleCloseReportEntry();
                      }} sx={{ color: '#1565C0', fontSize: 14 }}>
                        <ListItemIcon><CloudOffIcon fontSize="small" sx={{ color: '#1565C0' }} /></ListItemIcon>
                        Unpublish from portal
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={async () => {
                        try {
                          const updated = await ProjectService.publishReportToPortal(selectedEntry.id);
                          setReports(prev => prev.map(r => r.id === updated.id ? { ...r, publishedToPortal: updated.publishedToPortal } : r));
                          showToast('Sent to portal', 'success');
                        } catch { showToast('Failed to send to portal', 'error'); }
                        handleCloseReportEntry();
                      }} sx={{ color: '#1565C0', fontSize: 14 }}>
                        <ListItemIcon><CloudUploadIcon fontSize="small" sx={{ color: '#1565C0' }} /></ListItemIcon>
                        Send to portal
                      </MenuItem>
                    )
                  )}
                  <Divider />
                  <MenuItem onClick={() => { handleDeleteReportEntry(selectedEntry); handleCloseReportEntry(); }} sx={{ color: '#EF4444', fontSize: 14 }}>{t('common.delete')}</MenuItem>
                </Menu>
              </Paper>
            </Box>
          )}

          {/* ── Invoices (admin only) ── */}
          {role === Roles.ADMIN && pageTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 3 }}>
                <Box sx={{ mb: 2, display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, "& > *": { width: { xs: "100%", sm: "auto" } } }}>
                  <InvoiceStatusFilter value={selectedStatuses} onChange={setSelectedStatuses} />
                </Box>
                <Box className={styles.tableWrap}>
                  <Table size="small" className={styles.tableMin}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('invoices.status')}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('invoices.amountBeforeTax')}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('invoices.taxAmount')}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('invoices.totalPrice')}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('invoices.discount')}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('invoices.createdDate')}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('invoices.dueDate')}</TableCell>
                        <TableCell sx={{ width: 48 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredInvoices.map((invoice, index) => {
                        const invColor = INVOICE_STATUS_OPTIONS_COLORS.find(opt => opt.label === invoice.status)?.color || '#64748B';
                        return (
                          <TableRow key={index} sx={{
                            '&:hover': { backgroundColor: '#F8FAFC' },
                            '&:hover .row-actions': { opacity: 1 },
                            '& .row-actions': { opacity: 0, transition: 'opacity 0.15s' },
                          }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Tooltip title={(invoice?.status === INVOICE_STATUS.CANCELLED && invoice?.cancellationReason) ? t('invoices.cancellationReason', {reason: invoice?.cancellationReason}) : ''}>
                                  <Chip size="small" label={invoice?.status}
                                    sx={{ bgcolor: invColor + '18', color: invColor, fontWeight: 600, fontSize: 11, border: `1px solid ${invColor}30`, height: 22 }} />
                                </Tooltip>
                                {invoice?.publishedToPortal && (
                                  <Chip label="Portal" size="small"
                                    icon={<CloudUploadIcon style={{ fontSize: 11 }} />}
                                    sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, fontSize: 10, borderRadius: '6px', height: 20, '& .MuiChip-icon': { color: '#1565C0', ml: '6px' } }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, color: '#1E293B' }}>{invoice?.amountBeforeTax}</TableCell>
                            <TableCell sx={{ fontSize: 13, color: '#1E293B' }}>{invoice?.taxAmount}</TableCell>
                            <TableCell sx={{ fontSize: 13, color: '#1E293B', fontWeight: 600 }}>{invoice?.totalPrice}</TableCell>
                            <TableCell sx={{ fontSize: 13 }}>
                              {invoice?.rabattType
                                ? invoice.rabattType === "PERCENTAGE"
                                    ? <Typography sx={{ fontSize: 13, color: '#EF4444', fontWeight: 500 }}>-{invoice?.rabattValue}%</Typography>
                                    : invoice.rabattType === "AMOUNT"
                                        ? <Typography sx={{ fontSize: 13, color: '#EF4444', fontWeight: 500 }}>-{invoice?.rabattValue} CHF</Typography>
                                        : "—"
                                : "—"
                              }
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, color: '#64748B' }}>
                              {invoice?.createdAt ? new Date(invoice.createdAt).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, color: '#64748B' }}>
                              {invoice?.dueDate ? new Date(invoice.dueDate).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}
                            </TableCell>
                            <TableCell>
                              <Box className="row-actions" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                                <Tooltip title="Quick view">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenQuickView(invoice); }} sx={{ color: '#94A3B8', '&:hover': { color: '#427898', bgcolor: '#EFF6FF' } }}>
                                    <VisibilityIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMoreInvoiceEntryClick(e, invoice); }} sx={{ color: '#94A3B8', '&:hover': { color: '#427898' } }}>
                                  <MoreVertIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <Menu
                    anchorEl={anchorInvoiceEntryEl}
                    open={Boolean(anchorInvoiceEntryEl)}
                    onClose={handleCloseInvoiceEntry}
                    PaperProps={{ sx: { borderRadius: 1.5, border: '1px solid #EEF2F6', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: 200 } }}
                  >
                    <MenuItem onClick={() => { navigate(`/invoices/${selectedEntry?.id}`, { state: { invoice: selectedEntry, projectId: id } }); handleCloseInvoiceEntry(); }} sx={{ fontSize: 14 }}>
                      <ListItemIcon><OpenInNewIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                      Open detail page
                    </MenuItem>
                    <MenuItem onClick={() => { handleOpenQuickView(selectedEntry); handleCloseInvoiceEntry(); }} sx={{ fontSize: 14 }}>
                      <ListItemIcon><VisibilityIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                      Quick view
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem onClick={() => { handleDownloadInvoiceEntry(selectedEntry); handleCloseInvoiceEntry(); }} sx={{ fontSize: 14 }}>{t('common.download')}</MenuItem>
                    <MenuItem onClick={() => { handleShareInvoice(selectedEntry); handleCloseInvoiceEntry(); }} sx={{ fontSize: 14 }}>Share</MenuItem>
                    <MenuItem onClick={() => { handleEditInvoiceEntry(selectedEntry); handleCloseInvoiceEntry(); }} disabled={selectedEntry?.status === INVOICE_STATUS.CANCELLED} sx={{ fontSize: 14 }}>{t('common.edit')}</MenuItem>
                    <MenuItem onClick={() => { handleCancelInvoiceEntryModalOpen(selectedEntry); handleCloseInvoiceEntry(); }} disabled={[INVOICE_STATUS.CANCELLED, INVOICE_STATUS.PAID, INVOICE_STATUS.PARTIALLY_PAID, INVOICE_STATUS.WRITTEN_OFF].includes(selectedEntry?.status)} sx={{ fontSize: 14 }}>{t('invoices.cancel')}</MenuItem>
                    {customerPortalActive && (selectedEntry?.publishedToPortal ? (
                      <MenuItem onClick={() => { handleUnpublishInvoiceFromPortal(selectedEntry); handleCloseInvoiceEntry(); }} sx={{ color: '#1565C0', fontSize: 14 }}>
                        <ListItemIcon><CloudOffIcon fontSize="small" sx={{ color: '#1565C0' }} /></ListItemIcon>
                        Unpublish from portal
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={() => { handlePublishInvoiceToPortal(selectedEntry); handleCloseInvoiceEntry(); }} disabled={selectedEntry?.status === INVOICE_STATUS.CANCELLED} sx={{ color: '#1565C0', fontSize: 14 }}>
                        <ListItemIcon><CloudUploadIcon fontSize="small" sx={{ color: '#1565C0' }} /></ListItemIcon>
                        Send to portal
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Paper>
            </Box>
          )}

          {/* ── Notes ── */}
          {pageTab === (role === Roles.ADMIN ? 4 : 2) && (
            <Box sx={{ p: 3, maxWidth: 760, mx: 'auto' }}>
              <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 3 }}>
                <Box sx={{ mb: 2, maxHeight: 480, overflowY: 'auto', pr: 1 }}>
                  {notesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={22} sx={{ color: '#427898' }} />
                    </Box>
                  ) : projectNotes.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 2 }}>
                      No notes yet.
                    </Typography>
                  ) : (
                    projectNotes.map((note) => {
                      const isDeleted = note.deleted || note.deletedAt != null;
                      const isCrm = note.authorType === 'CRM_USER';
                      const isEditingThis = editingNoteId === note.id;
                      const canEdit = !isDeleted && isCrm;
                      return (
                        <Box key={note.id} sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexDirection: isCrm ? 'row' : 'row-reverse' }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: isCrm ? '#427898' : '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                            <Typography sx={{ color: 'white', fontSize: 11, fontWeight: 700 }}>{isCrm ? 'T' : 'C'}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, maxWidth: '80%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                              <Typography variant="caption" fontWeight={700} sx={{ color: '#475569' }}>
                                {isCrm ? 'Your team' : 'Customer'}
                              </Typography>
                              {!isCrm && <Chip label="SHARED" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, backgroundColor: '#e8f4e8', color: '#2e7d32' }} />}
                              {isCrm && note.visibility === 'INTERNAL' && <Chip label="Internal" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, backgroundColor: '#fef3c7', color: '#d97706' }} />}
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                {note.createdAt ? new Date(note.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                              </Typography>
                              {note.editedAt && !isDeleted && <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>(edited)</Typography>}
                            </Box>
                            <Paper elevation={0} sx={{ p: 1.5, backgroundColor: isCrm ? '#f1f5f9' : '#e8f4e8', border: '1px solid', borderColor: isCrm ? '#e2e8f0' : '#c8e6c9', borderRadius: 2 }}>
                              {isDeleted ? (
                                <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>[deleted]</Typography>
                              ) : isEditingThis ? (
                                <Box>
                                  <TextField fullWidth multiline minRows={2} value={editingNoteContent} onChange={e => setEditingNoteContent(e.target.value)} size="small" autoFocus sx={{ mb: 1 }} />
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="contained" onClick={() => handleSaveNoteEdit(note.id)} sx={{ backgroundColor: '#427898', textTransform: 'none', fontSize: 12 }}>Save</Button>
                                    <Button size="small" onClick={() => setEditingNoteId(null)} sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}>Cancel</Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ color: '#1a2332', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note.content}</Typography>
                              )}
                              {canEdit && !isEditingThis && (
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => { setEditingNoteId(note.id); setEditingNoteContent(note.content); }}>
                                      <EditIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => setConfirmDeleteNoteId(note.id)}>
                                      <CloseIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Paper>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                  <div ref={notesEndRef} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box>
                  <TextField
                    fullWidth multiline minRows={2}
                    placeholder="Write a note..."
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select value={newNoteVisibility} onChange={e => setNewNoteVisibility(e.target.value)} sx={{ fontSize: 13 }}>
                        <MenuItem value="SHARED">Shared (visible to customer)</MenuItem>
                        <MenuItem value="INTERNAL">Internal (CRM only)</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      disabled={addingNote || !newNoteContent.trim()}
                      onClick={handleAddProjectNote}
                      sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                      startIcon={addingNote ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                      Add Note
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* ── Tasks ── */}
          {role === Roles.ADMIN && pageTab === 5 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>Tasks</Typography>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openTaskAdd}
                  sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#427898', '&:hover': { bgcolor: '#356480' }, boxShadow: 'none', borderRadius: 1.5 }}>
                  Add Task
                </Button>
              </Box>
              {tasksLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} sx={{ color: '#427898' }} /></Box>
              ) : projectTasks.length === 0 ? (
                <Typography sx={{ color: '#94A3B8', fontSize: 14, textAlign: 'center', py: 4 }}>No tasks yet for this project.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {sortedTaskDates.map(dateKey => (
                    <Box key={dateKey}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                        {fmtTaskDateHeader(dateKey)}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {groupedProjectTasks[dateKey].map(task => {
                          const colors = TASK_STATUS_COLORS[task.status] || TASK_STATUS_COLORS.PLANNED;
                          return (
                            <Paper key={task.id} elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, p: 2, bgcolor: colors.bg, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{task.title}</Typography>
                                  <Chip label={task.status.replace('_', ' ')} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  {task.startTime && <Typography sx={{ fontSize: 12, color: '#64748B' }}>{task.startTime}{task.endTime ? `–${task.endTime}` : ''}</Typography>}
                                  {task.location && <Typography sx={{ fontSize: 12, color: '#64748B' }}>{task.location}</Typography>}
                                  {task.assignedEmployees?.length > 0 && (
                                    <Typography sx={{ fontSize: 12, color: '#64748B' }}>{task.assignedEmployees.map(e => `${e.firstName} ${e.lastName}`).join(', ')}</Typography>
                                  )}
                                </Box>
                                {task.description && <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.5 }}>{task.description}</Typography>}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <IconButton size="small" onClick={() => openTaskEdit(task)} sx={{ color: '#94A3B8', '&:hover': { color: '#427898' } }}>
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteTask(task.id)} sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

        </Box>

        {/* ── Task create/edit drawer ── */}
        <Drawer anchor="right" open={taskDrawerOpen} onClose={() => setTaskDrawerOpen(false)}
          PaperProps={{ sx: { width: 460, display: 'flex', flexDirection: 'column' } }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1E293B' }}>{editingTask ? 'Edit Task' : 'New Task'}</Typography>
            <IconButton size="small" onClick={() => setTaskDrawerOpen(false)} sx={{ color: '#94A3B8' }}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField label="Title *" value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} size="small" fullWidth inputProps={{ maxLength: 200 }} />
            <TextField label="Description" value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))} size="small" fullWidth multiline rows={2} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField label="Date *" type="date" value={taskForm.taskDate} onChange={e => setTaskForm(f => ({ ...f, taskDate: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
              <TextField label="Start" type="time" value={taskForm.startTime} onChange={e => setTaskForm(f => ({ ...f, startTime: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 110 }} />
              <TextField label="End" type="time" value={taskForm.endTime} onChange={e => setTaskForm(f => ({ ...f, endTime: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 110 }} />
            </Box>
            <Autocomplete
              multiple options={taskEmployees} getOptionLabel={e => `${e.firstName} ${e.lastName}`}
              value={taskEmployees.filter(e => taskForm.employeeIds.includes(e.id))}
              onChange={(_, v) => setTaskForm(f => ({ ...f, employeeIds: v.map(e => e.id) }))}
              size="small"
              renderInput={params => <TextField {...params} label="Assigned employees" />}
              renderTags={(value, getTagProps) => value.map((e, index) => (
                <Chip key={e.id} label={`${e.firstName} ${e.lastName}`} size="small" {...getTagProps({ index })} />
              ))}
            />
            <TextField label="Location (optional)" value={taskForm.location} onChange={e => setTaskForm(f => ({ ...f, location: e.target.value }))} size="small" fullWidth inputProps={{ maxLength: 300 }} />
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={taskForm.status} label="Status" onChange={e => setTaskForm(f => ({ ...f, status: e.target.value }))}>
                {[['PLANNED','Planned'],['IN_PROGRESS','In Progress'],['DONE','Done'],['CANCELLED','Cancelled']].map(([v,l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid #EEF2F6', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
            <Button onClick={() => setTaskDrawerOpen(false)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveTask} disabled={savingTask}
              sx={{ textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#356480' }, boxShadow: 'none', borderRadius: 1.5, minWidth: 90 }}>
              {savingTask ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : editingTask ? 'Save' : 'Create'}
            </Button>
          </Box>
        </Drawer>

        {/* Confirm delete note */}
        <Modal open={!!confirmDeleteNoteId} onClose={() => setConfirmDeleteNoteId(null)}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', bgcolor: 'white', borderRadius: 2, p: 3, width: 360, maxWidth: 'calc(100vw - 32px)', boxShadow: 24 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Delete Note</Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#475569' }}>Are you sure you want to delete this note?</Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setConfirmDeleteNoteId(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button onClick={() => handleDeleteNote(confirmDeleteNoteId)} color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
            </Box>
          </Box>
        </Modal>

        {/* Add/Edit Entry */}
        <WorkspaceDrawer
            open={openProjectEntriesModal}
            onClose={handleCloseProjectEntryModal}
            accentColor="#427898"
            title={selectedEntry?.id ? t('modals.editEntry') : t('modals.addEntry')}
            subtitle={t('projectInfoEntriesExpenses.tabs.entriesHours')}
            saveLabel={t('common.save')}
            onSave={handleSaveProjectEntry}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
                fullWidth size="small"
                label={t('labels.description')}
                name="description"
                value={selectedEntry?.description || ''}
                onChange={handleInputChange}
                error={!!validationErrors?.description}
                helperText={validationErrors?.description || ' '}
                multiline rows={3}
                autoFocus
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <DatePicker
                  selected={selectedEntry?.entryDate instanceof Date ? selectedEntry.entryDate : null}
                  onChange={(date) => setSelectedEntry(p => ({ ...p, entryDate: date }))}
                  dateFormat="dd.MM.yyyy"
                  customInput={<TextField label="Date" size="small" fullWidth />}
              />
              <TextField
                  fullWidth size="small"
                  label={t('labels.hours')}
                  name="workingHours"
                  type="number"
                  inputProps={{ min: 0, step: 0.5 }}
                  value={selectedEntry?.workingHours || ''}
                  onChange={handleInputChange}
                  error={!!validationErrors?.workingHours}
                  helperText={validationErrors?.workingHours || ' '}
                  InputProps={{
                      endAdornment: (
                          <InputAdornment position="end">
                              <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>h</Typography>
                          </InputAdornment>
                      ),
                  }}
              />
            </Box>
            <Box sx={{
                px: 1.5, py: 1.25, borderRadius: 1.5,
                bgcolor: selectedEntry?.billable !== false ? '#F0FDF4' : '#F8FAFC',
                border: `1px solid ${selectedEntry?.billable !== false ? '#BBF7D0' : '#E2E8F0'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                transition: 'all 0.15s',
            }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>Billable</Typography>
                <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>Included in invoice generation</Typography>
              </Box>
              <Switch
                  checked={selectedEntry?.billable !== false}
                  onChange={(e) => setSelectedEntry(p => ({ ...p, billable: e.target.checked }))}
                  sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#16A34A' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16A34A' },
                  }}
              />
            </Box>
          </Box>
        </WorkspaceDrawer>

        {/* Edit Project */}
        <Dialog
            open={isEditProjectModalOpen}
            onClose={handleCloseEditProjectModal}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #EEF2F6' }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#0F172A' }}>{t('modals.editProject')}</Typography>
            <IconButton size="small" onClick={handleCloseEditProjectModal} sx={{ color: '#94A3B8' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSaveProjectChanges(); }} sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
              <TextField
                  label={t('labels.projectName')}
                  fullWidth
                  value={editedProject?.name || ''}
                  onChange={(e) => setEditedProject((prev) => ({ ...prev, name: e.target.value }))}
              />
              <TextField
                  label={t('labels.description')}
                  fullWidth multiline rows={3}
                  value={editedProject?.description || ''}
                  onChange={(e) => setEditedProject((prev) => ({ ...prev, description: e.target.value }))}
              />
              <FormControl fullWidth>
                <Select
                    value={editedProject?.status || ''}
                    onChange={(e) => setEditedProject((prev) => ({ ...prev, status: e.target.value }))}
                    variant="outlined"
                    displayEmpty
                >
                  {PROJECT_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.label} value={option.label}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                  label={t('pricing.pricePerHour')}
                  fullWidth type="number"
                  value={editedProject?.pricePerHour ?? ''}
                  onChange={(e) => setEditedProject((prev) => ({ ...prev, pricePerHour: e.target.value }))}
              />
              <TextField
                  label={t('labels.contractedValue')}
                  fullWidth type="number"
                  value={editedProject?.contractedValue ?? ''}
                  onChange={(e) => setEditedProject((prev) => ({ ...prev, contractedValue: e.target.value }))}
              />
              <TextField
                  label={t('labels.budget')}
                  fullWidth type="number"
                  value={editedProject?.budget ?? ''}
                  onChange={(e) => setEditedProject((prev) => ({ ...prev, budget: e.target.value }))}
              />
              <DatePicker
                  selected={editedProject?.startDate || null}
                  onChange={(date) => setEditedProject((prev) => ({ ...prev, startDate: date }))}
                  customInput={<TextField label={t('labels.startDate')} fullWidth />}
              />
              <DatePicker
                  selected={editedProject?.endDate || null}
                  onChange={(date) => setEditedProject((prev) => ({ ...prev, endDate: date }))}
                  customInput={<TextField label={t('labels.endDate')} fullWidth />}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #EEF2F6' }}>
            <Button onClick={handleCloseEditProjectModal} sx={{ color: '#64748B', textTransform: 'none' }}>
              {t('common.dismiss')}
            </Button>
            <Button variant="contained" onClick={handleSaveProjectChanges}
              sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}>
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm modals */}
        <ConfirmModal
            open={isConfirmDeleteProjectEntryOpen}
            onClose={() =>
                !isDeletingProjectEntry && setConfirmDeleteProjectEntryOpen(false)
            }
            onConfirm={handleConfirmDeleteProjectEntry}
            question={t('confirm.deleteProjectEntry', {desc: selectedEntry?.description})}
            isLoading={isDeletingProjectEntry}
        />

        <ConfirmModal
            open={isConfirmDeleteProjectExpenseOpen}
            onClose={() =>
                !isDeletingProjectExpense && setConfirmDeleteProjectExpenseOpen(false)
            }
            onConfirm={handleConfirmDeleteProjectExpense}
            question={t('confirm.deleteProjectExpense', {desc: selectedExpense?.description})}
            isLoading={isDeletingProjectExpense}
        />

        <ConfirmModal
            open={isConfirmDeleteProjectOpen}
            onClose={() => !isDeletingProject && setConfirmDeleteProjectOpen(false)}
            onConfirm={handleConfirmDeleteProject}
            question={t('confirm.deleteProject', {name: project?.name})}
            isLoading={isDeletingProject}
        />

        <ConfirmModal
            open={isConfirmDeleteProjectServiceOpen}
            onClose={() => !isDeletingProjectService && setConfirmDeleteProjectServiceOpen(false)}
            onConfirm={handleConfirmDeleteProjectService}
            question={t('confirm.deleteSubservice', {desc: selectedService?.description})}
            isLoading={isDeletingProjectService}
        />

        <ConfirmModal
            open={isConfirmDeleteProjectInvoiceOpen}
            onClose={() =>
                !isDeletingProjectInvoice && setConfirmDeleteProjectInvoiceOpen(false)
            }
            onConfirm={handleConfirmDeleteProjectInvoice}
            question={t('confirm.deleteInvoice')}
            isLoading={isDeletingProjectInvoice}
        />

        {/* Edit invoice (status) */}

        <Modal open={isEditInvoiceModalOpen} onClose={handleCloseEditInvoiceModal}>
          <Box className={styles.sheet}>
            <Typography variant="h6" className={styles.title}>
              {t('invoices.updateInvoice')}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box className={styles.formStack}>
              <Box className={styles.fieldGroup}>
                <Typography sx={{ textAlign: 'left', mb: 0.5 }}>
                  {t('invoices.status')}
                </Typography>
                <FormControl fullWidth>
                  <Select
                      value={updateInvoice?.status}
                      onChange={(e) =>
                          setUpdateInvoice({ ...updateInvoice, status: e.target.value })
                      }
                  >
                    {INVOICE_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box className={styles.actions}>
              <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCloseEditInvoiceModal}
                  startIcon={<CloseIcon />}
              >
                {t('common.cancel')}
              </Button>

              <Button
                  variant="contained"
                  sx={{ backgroundColor: '#427898', color: 'white' }}
                  onClick={() => handleUpdateInvoice(updateInvoice)}
                  disabled={isGeneratingInvoice}
              >
                {isGeneratingInvoice ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                    t('common.update')
                )}
              </Button>
            </Box>
          </Box>
        </Modal>


        <GenerateInvoiceModal
            project={project}
            projectEntries={entries}
            projectExpenses={expenses}
            projectServices={services}
            projectMaterials={materials}
            open={isGenerateInvoiceModalOpen}
            onClose={handleCloseGenerateInvoiceModal}
            onSuccess={fetchProject}
        />

        {/* Report composition drawer */}
        <ReportCompositionDrawer
          open={reportDrawerOpen}
          onClose={() => setReportDrawerOpen(false)}
          project={project}
          editingReport={editingReport}
          entryUsers={entryUsers}
          onSaved={(saved) => {
            setReports(prev => {
              const exists = prev.find(r => r.id === saved.id);
              return exists ? prev.map(r => r.id === saved.id ? saved : r) : [saved, ...prev];
            });
          }}
        />

        {/* Add/Edit Expense */}
        <WorkspaceDrawer
            open={openProjectExpenseModal}
            onClose={handleCloseProjectExpenseModal}
            accentColor="#F59E0B"
            title={selectedExpense?.id ? t('modals.editExpense') : t('modals.addExpense')}
            subtitle={t('projectInfoEntriesExpenses.tabs.expenses')}
            saveLabel={t('common.save')}
            onSave={handleSaveProjectExpense}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
                fullWidth size="small"
                label={t('labels.description')}
                name="description"
                value={selectedExpense?.description || ''}
                onChange={handleExpenseInputChange}
                error={!!validationErrors.description}
                helperText={validationErrors.description || ' '}
                multiline rows={3}
                autoFocus
            />
            <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid #EEF2F6', bgcolor: '#FAFBFC' }}>
              <FormControlLabel
                  control={
                    <Checkbox
                        checked={isGratisExpense}
                        onChange={handleExpenseGratisChange}
                        name="isGratis"
                        sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#F59E0B' } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ color: '#0F172A' }}>{t('expenses.gratis')}</Typography>
                      <Typography variant="caption" sx={{ color: '#94A3B8' }}>Mark this expense as complimentary (no charge)</Typography>
                    </Box>
                  }
              />
            </Box>
            {!isGratisExpense && (
                <TextField
                    fullWidth size="small"
                    label={t('expenses.priceChf')}
                    name="price"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={selectedExpense?.price || ''}
                    onChange={handleExpenseInputChange}
                    error={!!validationErrors.price}
                    helperText={validationErrors.price || ' '}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Typography sx={{ fontSize: 13, color: '#94A3B8', fontWeight: 500 }}>CHF</Typography>
                            </InputAdornment>
                        ),
                    }}
                />
            )}
          </Box>
        </WorkspaceDrawer>

        {/* Add/Edit Service */}
        <WorkspaceDrawer
            open={openProjectServiceModal}
            onClose={handleCloseProjectServiceModal}
            accentColor="#8B5CF6"
            title={selectedService?.service ? t('modals.editService') : t('modals.addService')}
            subtitle={t('projectInfoEntriesExpenses.tabs.services')}
            saveLabel={t('common.save')}
            onSave={handleSaveProjectService}
            width={480}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Service type */}
            <FormControl fullWidth size="small" disabled={!!selectedService?.id}>
              <InputLabel>{t('services.service')}</InputLabel>
              <Select
                  label={t('services.service')}
                  value={selectedService?.service || ''}
                  onChange={(e) => handleServiceFieldChange('service', e.target.value)}
              >
                {allServices.map((service) => (
                    <MenuItem key={service.id} value={service.name}>{service.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!!selectedService?.id || !selectedService?.service}>
              <InputLabel>{t('services.subservice')}</InputLabel>
              <Select
                  label={t('services.subservice')}
                  value={selectedService?.subServiceId || ''}
                  onChange={(e) => handleServiceFieldChange('subServiceId', e.target.value)}
                  renderValue={(selected) => {
                    const sub = selectedServiceData?.subServices.find((s) => s.id === selected);
                    return sub ? sub.name : '';
                  }}
              >
                {selectedServiceData?.subServices.map((sub) => (
                    <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Pricing preview card */}
            {selectedService?.service && selectedServiceData && (() => {
              const sub = selectedServiceData.subServices.find(s => s.id === selectedService.subServiceId);
              if (!sub) return null;
              return (
                  <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 1.75, py: 1.25, borderRadius: 1.5,
                      bgcolor: '#F5F3FF', border: '1px solid #DDD6FE',
                  }}>
                    <Typography fontSize={12} color="#6D28D9" fontWeight={500}>
                      {role === Roles.ADMIN ? t('services.pricing') : t('services.unit')}
                    </Typography>
                    <Typography fontSize={14} fontWeight={700} color="#7C3AED">
                      {role === Roles.ADMIN ? `${sub.price} CHF / ${sub.unit}` : sub.unit || '—'}
                    </Typography>
                  </Box>
              );
            })()}

            <TextField
                fullWidth size="small"
                label={t('services.quantity')}
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={selectedService?.quantity || ''}
                onChange={(e) => handleServiceFieldChange('quantity', e.target.value)}
            />

            <TextField
                fullWidth size="small"
                label={t('labels.description')}
                multiline rows={3}
                value={selectedService?.description || ''}
                onChange={(e) => handleServiceFieldChange('description', e.target.value)}
            />
          </Box>
        </WorkspaceDrawer>

        <CancelInvoiceModal
            invoice={selectedEntry}
            isCancelInvoiceModalOpen={isCancelInvoiceModalOpen}
            onClose={() => setIsCancelInvoiceModalOpen(false)}
            onSuccess={fetchProject}
        />
        <ShareInvoiceModalSimple
            open={shareInvoiceModal}
            onClose={() => setShareInvoiceModal(false)}
            invoice={selectedEntry}
            contacts={contacts}
        />

        {/* ── Invoice Quick View Drawer ── */}
        <Drawer
          anchor="right"
          open={Boolean(quickViewInvoice)}
          onClose={handleCloseQuickView}
          PaperProps={{ sx: { width: { xs: '100vw', sm: 680 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)', display: 'flex', flexDirection: 'column' } }}
          slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
        >
          {/* accent bar */}
          <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />

          {/* header */}
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700} fontSize={15} color="#0F172A">Invoice</Typography>
              {quickViewInvoice && (() => {
                const invColor = INVOICE_STATUS_OPTIONS_COLORS.find(opt => opt.label === quickViewInvoice.status)?.color || '#64748B';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Chip size="small" label={quickViewInvoice.status}
                      sx={{ bgcolor: invColor + '18', color: invColor, fontWeight: 600, fontSize: 11, border: `1px solid ${invColor}30`, height: 20 }} />
                    {quickViewInvoice.dueDate && (
                      <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                        Due {new Date(quickViewInvoice.dueDate).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
                      CHF {quickViewInvoice.totalPrice}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
            <Tooltip title="Download">
              <IconButton size="small" onClick={() => handleDownloadInvoiceEntry(quickViewInvoice)} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open detail page">
              <IconButton size="small" onClick={() => { handleCloseQuickView(); navigate(`/invoices/${quickViewInvoice?.id}`, { state: { invoice: quickViewInvoice, projectId: id } }); }} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={handleCloseQuickView} sx={{ color: '#94A3B8' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* body: pdf iframe */}
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', bgcolor: '#F1F5F9' }}>
            {quickViewLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <CircularProgress size={28} sx={{ color: '#427898' }} />
                <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Loading PDF…</Typography>
              </Box>
            ) : quickViewPdfUrl ? (
              <iframe
                src={quickViewPdfUrl}
                title="Invoice PDF"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Could not load PDF.</Typography>
              </Box>
            )}
          </Box>
        </Drawer>

        {/* ── Report Quick View Drawer ── */}
        <Drawer
          anchor="right"
          open={Boolean(quickViewReport)}
          onClose={handleCloseReportQuickView}
          PaperProps={{ sx: { width: { xs: '100vw', sm: 680 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)', display: 'flex', flexDirection: 'column' } }}
          slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
        >
          <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700} fontSize={15} color="#0F172A">
                {quickViewReport?.displayName || quickViewReport?.name || 'Report'}
              </Typography>
              {quickViewReport && (() => {
                const STATUS_META = {
                  DRAFT: { label: 'Draft', color: '#94a3b8' },
                  PUBLISHED: { label: 'Published', color: '#64748b' },
                  SIGNED: { label: 'Signed', color: '#22c55e' },
                  REFUSED: { label: 'Refused', color: '#ef4444' },
                  CUSTOMER_NOT_AVAILABLE: { label: 'Not Available', color: '#f59e0b' },
                  AWAITING_REMOTE_SIGNATURE: { label: 'Awaiting Signature', color: '#3b82f6' },
                  REMOTE_SIGNATURE_EXPIRED: { label: 'Expired', color: '#f97316' },
                  CONFIRMED_WITHOUT_SIGNATURE: { label: 'Confirmed', color: '#64748b' },
                };
                const sm = STATUS_META[quickViewReport.status] || { label: quickViewReport.status, color: '#94a3b8' };
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Chip size="small" label={sm.label}
                      sx={{ bgcolor: sm.color + '18', color: sm.color, fontWeight: 600, fontSize: 11, border: `1px solid ${sm.color}30`, height: 20 }} />
                    {quickViewReport.type === 'FIELD_SERVICE'
                      ? <Chip size="small" label="Field Service" sx={{ bgcolor: '#42789818', color: '#427898', fontWeight: 600, fontSize: 10, height: 20 }} />
                      : <Chip size="small" label="Internal" sx={{ bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600, fontSize: 10, height: 20 }} />
                    }
                    {quickViewReport.reportDate && (
                      <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                        {new Date(quickViewReport.reportDate).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </Box>
            <Tooltip title="Download">
              <IconButton size="small" onClick={() => handleDownloadReportEntry(quickViewReport)} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open detail page">
              <IconButton size="small" onClick={() => { handleCloseReportQuickView(); navigate(`/reports/${quickViewReport?.id}`, { state: { report: quickViewReport, projectId: id } }); }} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={handleCloseReportQuickView} sx={{ color: '#94A3B8' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', bgcolor: '#F1F5F9' }}>
            {quickViewReportLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <CircularProgress size={28} sx={{ color: '#427898' }} />
                <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Loading PDF…</Typography>
              </Box>
            ) : quickViewReportPdfUrl ? (
              <iframe
                src={quickViewReportPdfUrl}
                title="Report PDF"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Could not load PDF.</Typography>
              </Box>
            )}
          </Box>
        </Drawer>

        {/* ── Remote signature dialog ── */}
        <Dialog
          open={remoteSignDialogOpen}
          onClose={() => !remoteSignSending && setRemoteSignDialogOpen(false)}
          maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
        >
          <Box sx={{ height: 4, bgcolor: '#427898', flexShrink: 0 }} />
          <DialogTitle sx={{ fontWeight: 700, color: '#1a2332', pb: 1 }}>
            Send for remote signature
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              A signature request will be sent to the selected email address via Skribble.
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                Send to
              </FormLabel>
              <RadioGroup value={remoteSignSelectedEmail} onChange={e => setRemoteSignSelectedEmail(e.target.value)}>
                {project?.customerContact?.email && (
                  <FormControlLabel
                    value={project.customerContact.email}
                    control={<Radio size="small" sx={{ color: '#427898', '&.Mui-checked': { color: '#427898' } }} />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{project.customerContact.email}</Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {project.customerContact.firstName} {project.customerContact.lastName} · Project contact
                        </Typography>
                      </Box>
                    }
                  />
                )}
                {contacts?.filter(c => c.email && c.email !== project?.customerContact?.email).map(contact => (
                  <FormControlLabel
                    key={contact.id || contact.email}
                    value={contact.email}
                    control={<Radio size="small" sx={{ color: '#427898', '&.Mui-checked': { color: '#427898' } }} />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{contact.email}</Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{contact.firstName} {contact.lastName}</Typography>
                      </Box>
                    }
                  />
                ))}
                <FormControlLabel
                  value="__custom__"
                  control={<Radio size="small" sx={{ color: '#427898', '&.Mui-checked': { color: '#427898' } }} />}
                  label={<Typography variant="body2" fontWeight={500}>Use a different email</Typography>}
                />
              </RadioGroup>
              {remoteSignSelectedEmail === '__custom__' && (
                <TextField
                  fullWidth variant="standard" label="Email address" type="email" autoFocus
                  value={remoteSignCustomEmail}
                  onChange={e => setRemoteSignCustomEmail(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setRemoteSignDialogOpen(false)} disabled={remoteSignSending}
              sx={{ textTransform: 'none', color: '#64748b' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendRemoteSignature}
              disabled={remoteSignSending || !remoteSignSelectedEmail || (remoteSignSelectedEmail === '__custom__' && !remoteSignCustomEmail.trim())}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
            >
              {remoteSignSending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send request'}
            </Button>
          </DialogActions>
        </Dialog>

      </Layout>
  );
};

export default ProjectInfo;
